import { useState, useCallback, useMemo, useEffect } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { BoardState, Card, CardStatus, FilterState, Lane, LaneId } from "../data/types";
import { flowNodes, flowEdges, dailyChecklist } from "../data/board-data";
import {
  filterCards,
  computeStatusCounts,
  computeKpiTotals,
  computeDependencyWarnings,
  computeChecklistProgress,
} from "../lib/boardUtils";
import type { Id } from "../../convex/_generated/dataModel";
import type { CSVImportResult } from "../lib/csvTasks";

// Lane definitions (static structure — cards come from Convex)
const LANE_DEFS: Array<{ id: LaneId; title: string; description: string; color: string }> = [
  { id: "objective_kpis", title: "Objectives & KPIs", description: "Launch week + month 1 targets", color: "#266DF0" },
  { id: "offer_pricing", title: "Offer & Pricing", description: "Open signup + 2-week beta annual offer", color: "#8B5CF6" },
  { id: "channels", title: "Channels", description: "Owned / rented / borrowed engine", color: "#06B6D4" },
  { id: "commercial_pipeline", title: "Commercial Pipeline", description: "Lead -> demo -> trial -> close", color: "#F59E0B" },
  { id: "weekly_content", title: "Weekly Content System", description: "Launch assets, daily narrative, proof system", color: "#10B981" },
  { id: "revops_tracking", title: "RevOps & Tracking", description: "Attio pipeline, UTM and reporting discipline", color: "#EF4444" },
  { id: "feedback_loop", title: "Product ↔ Sales Feedback", description: "Daily learning loop and iteration", color: "#EC4899" },
];

function today() {
  return new Date().toISOString().slice(0, 10);
}

export function useBoardConvex() {
  // ── Convex Queries ──
  const convexCards = useQuery(api.cards.list);
  const convexKpis = useQuery(api.kpis.list);
  const convexChecklist = useQuery(api.checklist.getByDate, { date: today() });

  // ── Convex Mutations ──
  const updateCardStatusMut = useMutation(api.cards.updateStatus);
  const updateCardMut = useMutation(api.cards.update);
  const createCardMut = useMutation(api.cards.create);
  const resetCardsMut = useMutation(api.cards.resetAll);
  const toggleChecklistMut = useMutation(api.checklist.toggle);
  const ensureDailyMut = useMutation(api.checklist.ensureDaily);

  // ── Local UI State ──
  const [filters, setFiltersState] = useState<FilterState>({
    owner: "all",
    status: "all",
    channel: "all",
    week: "all",
    search: "",
  });
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);

  // ── Map Convex cards to app Card type ──
  const cards: Card[] = useMemo(() => {
    if (!convexCards) return [];
    return convexCards.map((c) => ({
      id: c.externalId,
      _convexId: c._id,
      laneId: c.laneId as LaneId,
      title: c.title,
      description: c.description,
      status: c.status,
      owner: c.owner,
      priority: c.priority,
      targetDate: c.targetDate,
      completedDate: c.completedDate,
      channel: c.channel,
      channelTier: c.channelTier,
      channelType: c.channelType,
      url: c.url,
      utmCampaign: c.utmCampaign,
      tags: c.tags,
      dependsOn: c.dependsOn,
      notes: c.notes,
      weekNumber: c.weekNumber,
    }));
  }, [convexCards]);

  // Build a map from externalId -> convex _id
  const idMap = useMemo(() => {
    if (!convexCards) return new Map<string, Id<"cards">>();
    return new Map(convexCards.map((c) => [c.externalId, c._id]));
  }, [convexCards]);

  // ── Lanes with cards ──
  const lanes: Lane[] = useMemo(() => {
    return LANE_DEFS.map((def) => ({
      ...def,
      cards: cards.filter((c) => c.laneId === def.id),
    }));
  }, [cards]);

  // ── Build full state (for compatibility) ──
  const state: BoardState = useMemo(() => ({
    lanes,
    kpis: convexKpis ?? [],
    checklist: convexChecklist?.map((c) => ({
      id: c.itemId,
      _convexId: c._id,
      owner: c.owner,
      text: c.text,
      checked: c.checked,
      section: c.section,
    })) ?? dailyChecklist,
    filters,
    selectedCardId,
    flowNodes,
    flowEdges,
  }), [lanes, convexKpis, convexChecklist, filters, selectedCardId]);

  // ── Derive daily checklist items from recurring_daily cards ──
  const derivedChecklistItems = useMemo(() => {
    const recurringDaily = cards.filter((c) => c.tags.includes("task:recurring_daily"));
    if (recurringDaily.length > 0) {
      return recurringDaily.map((c) => ({
        itemId: c.id,
        owner: c.owner,
        text: c.title,
        section: (c.owner === "Joel" ? "joel" : c.owner === "Albert" ? "albert" : "joint") as "joel" | "albert" | "joint",
      }));
    }
    // Fallback to hardcoded dailyChecklist if no cards loaded yet
    return dailyChecklist.map((item) => ({
      itemId: item.id,
      owner: item.owner,
      text: item.text,
      section: item.section,
    }));
  }, [cards]);

  // ── Ensure daily checklist exists (effect, not memo) ──
  useEffect(() => {
    if (convexChecklist && convexChecklist.length === 0) {
      ensureDailyMut({
        date: today(),
        items: derivedChecklistItems,
      });
    }
  }, [convexChecklist, ensureDailyMut, derivedChecklistItems]);

  // ── Card Selection ──
  const selectCard = useCallback((cardId: string | null) => {
    setSelectedCardId(cardId);
  }, []);

  const selectedCard = useMemo(() => {
    if (!selectedCardId) return null;
    return cards.find((c) => c.id === selectedCardId) ?? null;
  }, [selectedCardId, cards]);

  // ── Card Status Update (Convex) ──
  const updateCardStatus = useCallback((cardId: string, newStatus: CardStatus) => {
    const convexId = idMap.get(cardId);
    if (convexId) {
      updateCardStatusMut({ id: convexId, status: newStatus });
    }
  }, [idMap, updateCardStatusMut]);

  // ── Card Field Update (Convex) ──
  const updateCard = useCallback((cardId: string, fields: Partial<Pick<Card, "title" | "description" | "notes" | "owner" | "priority" | "targetDate" | "tags" | "url" | "laneId">>) => {
    const convexId = idMap.get(cardId);
    if (convexId) {
      updateCardMut({ id: convexId, ...fields });
    }
  }, [idMap, updateCardMut]);

  // ── Archive Card ──
  const archiveCardMut = useMutation(api.cards.archive);
  const archiveCard = useCallback((cardId: string) => {
    const convexId = idMap.get(cardId);
    if (convexId) archiveCardMut({ id: convexId });
  }, [idMap, archiveCardMut]);

  // ── Unarchive Card ──
  const unarchiveCardMut = useMutation(api.cards.unarchive);
  const unarchiveCard = useCallback((cardId: string, restoreStatus: "planned" | "in_progress" = "planned") => {
    const convexId = idMap.get(cardId);
    if (convexId) unarchiveCardMut({ id: convexId, restoreStatus });
  }, [idMap, unarchiveCardMut]);

  // ── Delete Card ──
  const deleteCardMut = useMutation(api.cards.remove);
  const deleteCard = useCallback((cardId: string) => {
    const convexId = idMap.get(cardId);
    if (convexId) deleteCardMut({ id: convexId });
  }, [idMap, deleteCardMut]);

  // ── Create Card ──
  const createNewCard = useCallback(async (fields: {
    laneId: string;
    title: string;
    description: string;
    status: "planned" | "in_progress";
    owner: "Joel" | "Albert" | "AI Wave" | "Joint";
    priority: "critical" | "high" | "medium" | "low";
    tags: string[];
    notes: string;
  }) => {
    const externalId = `card-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    await createCardMut({
      ...fields,
      externalId,
      dependsOn: [],
    });
    return externalId;
  }, [createCardMut]);

  // ── Archive All Completed ──
  const archiveAllCompleted = useCallback(async () => {
    const doneCards = cards.filter((c) => c.status === "done");
    for (const card of doneCards) {
      const convexId = idMap.get(card.id);
      if (convexId) await archiveCardMut({ id: convexId });
    }
    return doneCards.length;
  }, [cards, idMap, archiveCardMut]);

  // ── Import Cards (from CSV parse result) ──
  const importCards = useCallback(async (importResult: CSVImportResult): Promise<{ created: number; skipped: number }> => {
    const existingIds = new Set(cards.map((c) => c.id));
    let created = 0;
    let skipped = 0;

    for (const card of importResult.cards) {
      if (existingIds.has(card.id)) {
        skipped++;
        continue;
      }
      await createCardMut({
        externalId: card.id,
        laneId: card.laneId,
        title: card.title,
        description: card.description,
        status: card.status,
        owner: card.owner,
        priority: card.priority,
        targetDate: card.targetDate,
        channel: card.channel,
        channelTier: card.channelTier,
        channelType: card.channelType,
        url: card.url,
        utmCampaign: card.utmCampaign,
        tags: card.tags,
        dependsOn: card.dependsOn,
        notes: card.notes,
        weekNumber: card.weekNumber,
      });
      created++;
    }

    return { created, skipped };
  }, [cards, createCardMut]);

  const resetCards = useCallback(async (): Promise<number> => {
    return resetCardsMut({});
  }, [resetCardsMut]);

  // ── Filters ──
  const setFilters = useCallback((updates: Partial<FilterState>) => {
    setFiltersState((f) => ({ ...f, ...updates }));
  }, []);

  const resetFilters = useCallback(() => {
    setFiltersState({ owner: "all", status: "all", channel: "all", week: "all", search: "" });
  }, []);

  // ── Filtered Lanes ──
  const filteredLanes = useMemo(() => {
    return lanes.map((lane) => ({
      ...lane,
      cards: filterCards(lane.cards, filters),
    }));
  }, [lanes, filters]);

  // ── Checklist ──
  const toggleChecklist = useCallback((itemId: string) => {
    if (!convexChecklist) return;
    const item = convexChecklist.find((c) => c.itemId === itemId);
    if (item) {
      toggleChecklistMut({ id: item._id, checked: !item.checked });
    }
  }, [convexChecklist, toggleChecklistMut]);

  const checklistProgress = useMemo(
    () => computeChecklistProgress(state.checklist),
    [state.checklist],
  );

  // ── KPI Aggregates ──
  const kpiTotals = useMemo(
    () => computeKpiTotals(state.kpis),
    [state.kpis],
  );

  // ── Dependency Warnings ──
  const dependencyWarnings = useMemo(
    () => computeDependencyWarnings(cards),
    [cards],
  );

  // ── Status Counts ──
  const statusCounts = useMemo(
    () => computeStatusCounts(cards),
    [cards],
  );

  // Loading state
  const isLoading = !convexCards || !convexKpis;

  return {
    state,
    filteredLanes,
    selectedCard,
    selectCard,
    updateCardStatus,
    updateCard,
    archiveCard,
    unarchiveCard,
    deleteCard,
    createNewCard,
    archiveAllCompleted,
    importCards,
    resetCards,
    setFilters,
    resetFilters,
    toggleChecklist,
    checklistProgress,
    kpiTotals,
    dependencyWarnings,
    statusCounts,
    isLoading,
  };
}
